import { skipToken } from "@reduxjs/toolkit/query/react";
import type { InterviewLanguage } from "@repo/app-types/constants/languages";
import { InferenceStatus } from "@repo/app-types/entities/jobType";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import { Label } from "@repo/ui/Label/label";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../../api/entities/interviewSectionDefinition.js";
import { OverviewBox } from "../../../../components/overviewBox.js";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hook.js";
import { useRefreshProjectWhile } from "../../../../hooks/useRefreshProjectWhile.js";
import { AssessmentSettingsOverview } from "../../../assessments/assessmentSettingsOverview.js";
import { selectAssessmentById } from "../../../assessments/assessmentsSlice.js";
import { JobTypeEditorSlideover } from "../../../jobTypes/editorSlideover.js";
import { retryInferenceForProjectById } from "../../projectsSlice.js";
import { RecruitingInterviewSectionsOverview } from "./recruitingInterviewSectionsOverview.js";

const NUM_QUESTIONS_TO_DISPLAY = 4;

/** If the job type hasn't been updated for a while, assume inference has timed out.  */
const INFERENCE_TIMEOUT = 2 * 60 * 1000; // 2 minutes

interface RecruitingSettingsQuestionsSectionProps {
  project: ProjectWithInterviewCount;
  language: InterviewLanguage;
  onSaveProject: (changes: ProjectUpdate) => void;
}

export const RecruitingSettingsQuestionsSection: FC<
  RecruitingSettingsQuestionsSectionProps
> = ({ project, language, onSaveProject }) => {
  const dispatch = useAppDispatch();

  const { data: fetchedSections, isLoading: isFetchingSections } =
    useAllInterviewSectionDefinitionsForJobTypeQuery(
      // Skip the query if there's no job type ID
      project.job_type?.id ? { jobTypeId: project.job_type.id } : skipToken,
    );

  const assessmentStatus = project.job_type?.settings.assessment_status;
  const assessmentId = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.AssessmentId,
  );
  const assessment = useAppSelector(selectAssessmentById(assessmentId));
  const isAssessmentInProgress =
    assessmentId === null &&
    assessmentStatus === InferenceStatus.InProgress &&
    checkIsJobTypeUpdatedInLast(project, INFERENCE_TIMEOUT);
  const isAssessmentFailed =
    assessmentId === null && assessmentStatus === InferenceStatus.Failed;

  const questionsStatus = project.job_type?.settings.questions_status;
  const questions = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.Questions,
  );
  const isQuestionsInProgress =
    questions.length === 0 &&
    questionsStatus === InferenceStatus.InProgress &&
    checkIsJobTypeUpdatedInLast(project, INFERENCE_TIMEOUT);
  const isQuestionsFailed =
    questions.length === 0 && questionsStatus === InferenceStatus.Failed;

  /** Whether we have yet to auto-generate assessment criteria and/or questions. */
  const isInferenceIncomplete =
    isAssessmentInProgress ||
    isAssessmentFailed ||
    isQuestionsInProgress ||
    isQuestionsFailed;

  /** Whether auto-generating assessment criteria and/or questions is in progress (as opposed to failed). */
  const isInferenceInProgress = isAssessmentInProgress || isQuestionsInProgress;

  useRefreshProjectWhile(project.id, isInferenceInProgress, INFERENCE_TIMEOUT);

  const [isInferenceRetrying, setIsInferenceRetrying] = useState(false);
  const onInferenceRetry = async () => {
    setIsInferenceRetrying(true);
    try {
      await dispatch(retryInferenceForProjectById(project.id));
    } finally {
      setIsInferenceRetrying(false);
    }
  };

  const [isQuestionsManagerSlideoverOpen, setIsQuestionsManagerSlideoverOpen] =
    useState(false);

  return (
    <div className="flex flex-col justify-between">
      <div className="flex flex-col space-y-3">
        <div>
          <Label size={Size.SMALL} className="mb-2">
            Job description
          </Label>

          <OverviewBox
            isDisabled={isInferenceIncomplete}
            onClick={() => setIsQuestionsManagerSlideoverOpen(true)}
          >
            <div className="px-3 py-2">
              <span className="line-clamp-3">
                {getProjectAndJobTypeProperty(
                  project,
                  JobTypeProperty.JobDescription,
                )}
              </span>
            </div>
          </OverviewBox>
        </div>
        <AssessmentSettingsOverview
          project={project}
          assessment={assessment}
          isDisabled={isInferenceIncomplete}
          isLoading={isAssessmentInProgress || isInferenceRetrying}
          isFailed={isAssessmentFailed}
          onRetryClick={onInferenceRetry}
          onEditClick={() => setIsQuestionsManagerSlideoverOpen(true)}
        />
        <RecruitingInterviewSectionsOverview
          questions={questions}
          interviewSections={fetchedSections || []}
          isDisabled={isInferenceIncomplete}
          isLoading={isQuestionsInProgress || isInferenceRetrying}
          isFailed={isQuestionsFailed}
          onRetryClick={onInferenceRetry}
          onEditClick={() => setIsQuestionsManagerSlideoverOpen(true)}
        />
      </div>

      <JobTypeEditorSlideover
        basicQuestions={questions}
        projectId={project.id}
        jobTypeId={project.job_type?.id}
        language={language}
        assessment={assessment ?? null}
        onSaveProject={onSaveProject}
        isOpen={isQuestionsManagerSlideoverOpen}
        onClose={() => {
          setIsQuestionsManagerSlideoverOpen(false);
        }}
        jobDescription={getProjectAndJobTypeProperty(
          project,
          JobTypeProperty.JobDescription,
        )}
      />
    </div>
  );
};

function checkIsJobTypeUpdatedInLast(
  project: ProjectWithInterviewCount,
  duration: number,
) {
  const jobTypeUpdatedAt =
    project.job_type?.updated_at ?? project.job_type?.created_at;

  return jobTypeUpdatedAt !== undefined
    ? Date.now() - new Date(jobTypeUpdatedAt).getTime() < duration
    : false;
}
